/**
 * Section "Actions" : boutons primaires sur le dossier.
 *  - Generer Devis (S3.3 : POST /generate -> download)
 *  - Generer AH (S3.3 : idem)
 *  - Marquer transmis au mandataire (S3.3 : POST /sent-to-mandataire)
 *  - Marquer bloque / Debloquer (avec prompt raison)
 *  - Relancer client (mailto: avec template basique)
 */

import { useState } from 'react';
import * as ceeApi from '../../api/cee.js';
import { downloadDocumentUrl } from '../lib/ceeUrls.js';

const RELANCE_SUBJECT_PREFIX = '[CEE TRA-EQ-131] Relance pieces administratives';

/**
 * Construit l URL mailto: pour relance, ou null si pas d email destinataire.
 * Codex review S2.6 P3 : avant on retournait `mailto:?subject=...&body=...` meme
 * sans email, ce qui ouvrait un client mail vide. Maintenant on retourne null
 * et le caller masque le bouton.
 *
 * @param {object} dossier
 * @returns {string | null}
 */
function buildMailto(dossier) {
  const c = dossier.client || {};
  const to = c.email;
  if (!to || typeof to !== 'string' || !to.trim()) return null;
  const subject = encodeURIComponent(`${RELANCE_SUBJECT_PREFIX} - ${c.name || ''}`);
  const body = encodeURIComponent(
    `Bonjour,\n\nNous attendons les pieces administratives pour finaliser votre dossier CEE.\n\n`
    + `Pour toute question, vous pouvez nous contacter directement.\n\nBien cordialement,\nJMJ Consulting`,
  );
  return `mailto:${to.trim()}?subject=${subject}&body=${body}`;
}

/**
 * @param {{ dossier: object | null, onChange: () => Promise<void> | void, readOnly?: boolean }} props
 */
export default function ActionsSection({ dossier, onChange, readOnly = false }) {
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  if (!dossier || readOnly) return null;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const wrap = async (id, fn) => {
    setBusy(id);
    setError('');
    try {
      await fn();
      await onChange();
    } catch (err) {
      setError(err.message || 'Action echouee');
    } finally {
      setBusy('');
    }
  };

  const onGenerate = (type) => wrap(`gen-${type}`, async () => {
    const out = await ceeApi.generateDoc(dossier.id, type);
    showToast(`${type === 'devis' ? 'Devis' : 'AH'} genere`);
    // Trigger download
    if (out && out.documentId) {
      const url = downloadDocumentUrl(dossier.id, out.documentId);
      // Force download via creation d un <a>
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Scroll vers DocumentsSection (id #docs-section, defini dans CeeDossierDetailPage)
      const docs = document.getElementById('docs-section');
      if (docs && docs.scrollIntoView) docs.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  const onMarkTransmis = () => wrap('transmis', async () => {
    await ceeApi.markSentToMandataire(dossier.id);
    showToast('Marque transmis au mandataire');
  });

  const onToggleBloque = () => {
    if (dossier.status === 'bloque') {
      // Debloquer = repasser sur recompute (laisse le backend choisir)
      return wrap('unblock', () => ceeApi.updateDossier(dossier.id, { status: null, blockingReason: null }));
    }
    const reason = window.prompt('Raison du blocage ?');
    if (!reason || !reason.trim()) return undefined;
    return wrap('block', () => ceeApi.updateDossier(dossier.id, { status: 'bloque', blockingReason: reason.trim() }));
  };

  return (
    <section className="mb-6">
      <h2 className="text-lg font-bold text-[#0f172a] mb-3">Actions</h2>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 mb-3 text-sm">
          {error}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onGenerate('devis')} disabled={!!busy}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {busy === 'gen-devis' ? 'Generation...' : 'Generer Devis'}
        </button>
        <button type="button" onClick={() => onGenerate('ah')} disabled={!!busy}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {busy === 'gen-ah' ? 'Generation...' : 'Generer AH'}
        </button>
        <button type="button" onClick={onMarkTransmis} disabled={!!busy}
          className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
          {busy === 'transmis' ? '...' : 'Marquer transmis'}
        </button>
        <button type="button" onClick={onToggleBloque} disabled={!!busy}
          className={`px-3 py-2 text-sm rounded-lg disabled:opacity-50 ${
            dossier.status === 'bloque'
              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              : 'bg-red-100 text-red-700 hover:bg-red-200'
          }`}>
          {dossier.status === 'bloque' ? 'Debloquer' : 'Marquer bloque'}
        </button>
        {(() => {
          const href = buildMailto(dossier);
          if (!href) {
            return (
              <span
                className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 text-gray-400 rounded-lg cursor-not-allowed"
                title="Pas d email enregistre pour ce client"
              >
                Relancer client (pas d email)
              </span>
            );
          }
          return (
            <a href={href} className="px-3 py-2 text-sm bg-white border border-gray-200 text-[#475569] rounded-lg hover:bg-gray-50">
              Relancer client
            </a>
          );
        })()}
      </div>
      {toast && (
        <div className="mt-3 text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded-md inline-block">{toast}</div>
      )}
    </section>
  );
}
